import { useMemo } from "react";
import { ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { DevUpdate } from "@/services/dev-updates.service";
import { TYPE_VARIANT, formatDate, dayKey } from "./dev-update-format";

export default function TimelineView({ updates }: { updates: DevUpdate[] }) {
	const groups = useMemo(() => {
		const map = new Map<string, DevUpdate[]>();
		for (const u of updates) {
			const key = dayKey(u.date);
			const list = map.get(key);
			if (list) list.push(u);
			else map.set(key, [u]);
		}
		return Array.from(map.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1));
	}, [updates]);

	return (
		<div className="space-y-6">
			{groups.map(([key, items]) => (
				<div key={key} className="relative pl-6">
					<div className="absolute left-0 top-1.5 h-2 w-2 rounded-full bg-primary" />
					<div className="absolute left-[3px] top-3 h-full w-px bg-border" />
					<h4 className="mb-3 text-sm font-semibold text-foreground">
						{formatDate(items[0].date)}
						<span className="ml-2 text-xs font-normal text-muted">
							{items.length} update{items.length > 1 ? "s" : ""}
						</span>
					</h4>
					<ul className="space-y-2">
						{items.map((u) => (
							<li
								key={u.id}
								className="rounded-md border border-border bg-surface p-3"
							>
								<div className="flex items-start justify-between gap-2">
									<div className="min-w-0">
										<div className="flex flex-wrap items-center gap-2">
											<Badge variant={TYPE_VARIANT[u.type] ?? "default"}>
												{u.type}
											</Badge>
											<span className="text-xs text-muted">{u.app}</span>
										</div>
										<p className="mt-1 font-medium text-foreground">
											{u.change}
										</p>
										{u.description && (
											<p className="mt-0.5 line-clamp-2 text-sm text-muted">
												{u.description}
											</p>
										)}
									</div>
									{u.url && (
										<a
											href={u.url}
											target="_blank"
											rel="noreferrer"
											className="shrink-0 text-muted hover:text-foreground"
										>
											<ExternalLink className="h-4 w-4" />
										</a>
									)}
								</div>
							</li>
						))}
					</ul>
				</div>
			))}
		</div>
	);
}
